'use strict';

/**
 * LoopMarkdown
 * ------------
 * Converts a kit's loop checklist templates ({ <loopkey>: [ { Title, Children? } ] }) to and from a
 * single markdown document, so the loops can be edited in the rich markdown editor as checklists:
 *
 *   ## Design
 *   - [ ] Problem and audience written down
 *   - [ ] Core flow sketched
 *     - [ ] Happy path
 *     - [ ] Key edge cases
 *
 *   ## Build
 *   - [ ] ...
 *
 * Each `## Heading` is matched (case/space-insensitive) to a loop's Label or Key; bullet or checklist
 * items (`-`, `*`, `+`, with an optional `[ ]` / `[x]`) become the loop's items, nested by indentation.
 * Pure functions; the server still receives and re-sanitizes the structured tree.
 */

function _norm(pStr) { return String(pStr == null ? '' : pStr).toLowerCase().replace(/[^a-z0-9]/g, ''); }

function _itemsToLines(pItems, pDepth)
{
	let tmpLines = [];
	let tmpIndent = '  '.repeat(pDepth);
	(Array.isArray(pItems) ? pItems : []).forEach((pItem) =>
	{
		if (!pItem || !pItem.Title) { return; }
		tmpLines.push(tmpIndent + '- [ ] ' + pItem.Title);
		if (Array.isArray(pItem.Children) && pItem.Children.length)
		{
			tmpLines = tmpLines.concat(_itemsToLines(pItem.Children, pDepth + 1));
		}
	});
	return tmpLines;
}

// A loops object -> one markdown document with a `## Label` section per loop (in LoopMeta order).
function loopsToMarkdown(pLoops, pLoopMeta)
{
	let tmpLoops = (pLoops && typeof pLoops === 'object') ? pLoops : {};
	let tmpLines = [];
	(Array.isArray(pLoopMeta) ? pLoopMeta : []).forEach((pLoop) =>
	{
		tmpLines.push('## ' + pLoop.Label);
		tmpLines = tmpLines.concat(_itemsToLines(Array.isArray(tmpLoops[pLoop.Key]) ? tmpLoops[pLoop.Key] : [], 0));
		tmpLines.push('');
	});
	return tmpLines.join('\n').replace(/\n+$/, '\n');
}

// Markdown -> a loops object keyed by loop key. Headings switch the current loop; bullet/checklist
// lines become items, nested by their relative indentation.
function markdownToLoops(pMarkdown, pLoopMeta)
{
	let tmpMeta = Array.isArray(pLoopMeta) ? pLoopMeta : [];
	let tmpLabelToKey = {};
	tmpMeta.forEach((pLoop) => { tmpLabelToKey[_norm(pLoop.Label)] = pLoop.Key; tmpLabelToKey[_norm(pLoop.Key)] = pLoop.Key; });
	let tmpResult = {};
	tmpMeta.forEach((pLoop) => { tmpResult[pLoop.Key] = []; });
	let tmpCurrentKey = tmpMeta.length ? tmpMeta[0].Key : null;
	let tmpStack = [];
	String(pMarkdown == null ? '' : pMarkdown).split('\n').forEach((pLine) =>
	{
		let tmpHeading = pLine.match(/^\s*#{1,6}\s+(.*\S)\s*$/);
		if (tmpHeading)
		{
			let tmpKey = tmpLabelToKey[_norm(tmpHeading[1])];
			if (tmpKey) { tmpCurrentKey = tmpKey; tmpStack = []; }
			return;
		}
		let tmpBullet = pLine.match(/^(\s*)[-*+]\s+(?:\[[ xX]?\]\s+)?(.*\S)\s*$/);
		if (!tmpBullet || !tmpCurrentKey || !tmpResult[tmpCurrentKey]) { return; }
		let tmpDepth = tmpBullet[1].replace(/\t/g, '  ').length;
		let tmpTitle = tmpBullet[2].trim();
		if (!tmpTitle) { return; }
		let tmpNode = { Title: tmpTitle };
		while (tmpStack.length && tmpStack[tmpStack.length - 1].depth >= tmpDepth) { tmpStack.pop(); }
		if (tmpStack.length)
		{
			let tmpParent = tmpStack[tmpStack.length - 1].node;
			if (!Array.isArray(tmpParent.Children)) { tmpParent.Children = []; }
			tmpParent.Children.push(tmpNode);
		}
		else
		{
			tmpResult[tmpCurrentKey].push(tmpNode);
		}
		tmpStack.push({ depth: tmpDepth, node: tmpNode });
	});
	return tmpResult;
}

// One loop's items -> a markdown checklist (no `## heading`; each editor is bound to a single loop).
function itemsToMarkdown(pItems)
{
	let tmpLines = _itemsToLines(pItems, 0);
	return tmpLines.length ? (tmpLines.join('\n') + '\n') : '';
}

// A markdown checklist (no heading) -> one loop's nested item tree, nested by relative indentation.
function markdownToItems(pMarkdown)
{
	let tmpRoot = [];
	let tmpStack = [];
	String(pMarkdown == null ? '' : pMarkdown).split('\n').forEach((pLine) =>
	{
		let tmpBullet = pLine.match(/^(\s*)[-*+]\s+(?:\[[ xX]?\]\s+)?(.*\S)\s*$/);
		if (!tmpBullet) { return; }
		let tmpDepth = tmpBullet[1].replace(/\t/g, '  ').length;
		let tmpTitle = tmpBullet[2].trim();
		if (!tmpTitle) { return; }
		let tmpNode = { Title: tmpTitle };
		while (tmpStack.length && tmpStack[tmpStack.length - 1].depth >= tmpDepth) { tmpStack.pop(); }
		if (tmpStack.length)
		{
			let tmpParent = tmpStack[tmpStack.length - 1].node;
			if (!Array.isArray(tmpParent.Children)) { tmpParent.Children = []; }
			tmpParent.Children.push(tmpNode);
		}
		else { tmpRoot.push(tmpNode); }
		tmpStack.push({ depth: tmpDepth, node: tmpNode });
	});
	return tmpRoot;
}

module.exports =
{
	loopsToMarkdown: loopsToMarkdown,
	markdownToLoops: markdownToLoops,
	itemsToMarkdown: itemsToMarkdown,
	markdownToItems: markdownToItems
};
